/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { createProvisioningPageExtensions } from '@atlassiansox/cross-flow-extensions/ProvisioningPageExtensions';
import { Journeys, useCrossFlow } from '@atlassiansox/cross-flow-support/cross-flow-api-internals';
import { Targets } from '@atlassiansox/cross-flow-support/cross-flow-base-types';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { PROJECTS_NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-id.tsx';
import {
	DROPDOWN_ITEM,
	NAVIGATION_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM,
	CUSTOM_ITEM_BUTTON,
	CUSTOM_ITEM_DROPDOWN_ACTION,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	DISMISS_RECOMMENDATION,
	MEATBALL_DROPDOWN,
	NEW_DISCOVERY_PROJECT,
	WHY_AM_I_SEEING_THIS,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import { PROJECT_RECOMMENDATION } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import {
	LEVEL_ONE,
	LEVEL_THREE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import type { JiraLinkConfig } from '@jira/platform__routing-router-components/src/ui/router-link-config/index.tsx';
import { Text } from '@atlaskit/primitives/compiled/text';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Button from '@atlaskit/button/button';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics/cross-flow/hooks';
import { fg } from '@atlassian/jira-feature-gating';
import type { AppRecommendations, RecommendationContext } from '../../common/types.tsx';
import { RecommendationActionMenu } from '../../common/ui/recommendation-action-menu/recommendation-action-menu.tsx';
import { DATA_PRIVACY_LINK, PACKAGE_NAME } from '../../constants.tsx';
import {
	JPD_ENTITY_ID,
	JPD_RECOMMENDATION_DISMISS_USER_PROPERTY,
	JPD_RECOMMENDATION_KEY,
	JPD_ROADMAP_KEY,
	getProductTypeCcpReferenceIdMapForEnv,
} from './constants.tsx';
import { hasJpdLicense } from './utils.tsx';
import ProductRoadmapProjectAvatar from './illustrations/product-roadmap-project-avatar.tsx';
import { Behaviours, Prompts, TargetTabs } from '@atlassiansox/cross-flow-extensions/types';

const projectTitleProductRoadmap = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.project-title-product-roadmap',

	defaultMessage: 'Create a roadmap',
	description: 'Title text for the JPD roadmap recommendation item shown in the sidebar navigation',
});

const lozengeText = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.lozenge-text',

	defaultMessage: 'try',
	description:
		'Text shown in a lozenge badge next to the JPD recommendation item in the sidebar, indicating users should try the product',
});

const reasonsModalTitle = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-title',

	defaultMessage: 'Why am I seeing this?',
	description:
		'Heading of the modal dialog that explains why the JPD recommendation is being shown to the user',
});

const reasonsModalExplanation = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-explanation',
	defaultMessage:
		"We think <strong>Jira Product Discovery</strong> will improve your team's productivity. The recommendation is based on:",
	description:
		'Explanatory text in the reasons modal describing why Jira Product Discovery is recommended. Contains a <strong> tag wrapping the product name.',
});

const reasonsModalBulletOneSpork = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-bullet-one-spork',

	defaultMessage: 'the tools you use (Jira)',
	description:
		'First bullet point in the reasons modal listing one of the factors behind the JPD recommendation',
});

const reasonsModalBulletTwo = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-bullet-two',

	defaultMessage: 'the way you use your existing tools and products',
	description:
		'Second bullet point in the reasons modal listing another factor behind the JPD recommendation',
});

const privacyPolicy = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.privacy-policy',

	defaultMessage: 'Learn more about your data privacy with Atlassian',
	description:
		'Link text in the reasons modal that navigates to the Atlassian data privacy policy page',
});

const reasonsModalButtonOk = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-button-ok',

	defaultMessage: 'Ok',
	description:
		'Label for the confirmation button that closes the reasons modal explaining the JPD recommendation',
});

const reasonsModalBulletTwoAppifyMessage = defineMessage({
	id: 'atlassian-navigation-recommendations.jpd-recommendation.reasons-modal-bullet-two-appify',

	defaultMessage: 'the way you use your existing tools and apps',
	description:
		'Second bullet point in the reasons modal listing another factor behind the JPD recommendation, using app terminology',
});

export const JpdRecommendation = ({
	recommendationKey,
	dismissRecommendation,
	sessionId,
}: {
	recommendationKey: string;
	dismissRecommendation: () => void;
	sessionId?: string | null;
}): React.JSX.Element => {
	const reasonsModalBulletTwoAppify = fg('product-terminology-refresh')
		? reasonsModalBulletTwoAppifyMessage
		: reasonsModalBulletTwo;
	const { formatMessage } = useIntl();
	const crossFlow = useCrossFlow();
	const accountId = useAccountId();
	const environment = useEnvironment();
	const jpdCloudCCPProductID = getProductTypeCcpReferenceIdMapForEnv(environment).PRODUCT_DISCOVERY;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	useEffect(() => {
		if (crossFlow.isEnabled) {
			fireViewedUIEvent(
				{
					action: 'viewed',
					actionSubject: 'section',
					actionSubjectId: 'projectsMenu',
				},
				{
					recommendedProductIds: [jpdCloudCCPProductID],
					source: 'projectsMenuSection',
					recommendationSession: {
						id: sessionId ?? '',
						type: 'appRec',
					},
				},
				{
					navigationVersion: 'v4',
					projectKey: JPD_ROADMAP_KEY,
				},
			);
		} else {
			fireErrorAnalytics({
				error: new Error(crossFlow.reason),
				meta: {
					id: crossFlow.reason,
					packageName: 'jiraAtlassianNavigationRecommendations',
				},
			});
		}
	}, [createAnalyticsEvent, crossFlow, fireViewedUIEvent, jpdCloudCCPProductID, sessionId]);
	const jpdRecommendationContent = useMemo(() => {
		return {
			projectTitle: projectTitleProductRoadmap,
			projectKey: JPD_ROADMAP_KEY,
			projectAvatar: ProductRoadmapProjectAvatar,
		};
	}, []);

	const onLearnMoreClicked = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: DROPDOWN_ITEM,
				action: 'clicked',
			}),
			PROJECTS_NAVIGATION_ITEM,
			{
				level: LEVEL_THREE,
				componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
				parentItemType: PROJECT_RECOMMENDATION,
				parentItemId: NEW_DISCOVERY_PROJECT,
				itemId: WHY_AM_I_SEEING_THIS,
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
		);
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'learnMore',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
				recommendationSession: {
					id: sessionId ?? '',
					type: 'appRec',
				},
			},
			{
				navigationVersion: 'v4',
			},
		);
	}, [createAnalyticsEvent, fireInteractedUIEvent, jpdCloudCCPProductID, sessionId]);

	const onDismissClicked = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: DROPDOWN_ITEM,
				action: 'clicked',
			}),
			PROJECTS_NAVIGATION_ITEM,
			{
				level: LEVEL_THREE,
				componentType: CUSTOM_ITEM_DROPDOWN_ACTION,
				parentItemType: PROJECT_RECOMMENDATION,
				parentItemId: NEW_DISCOVERY_PROJECT,
				itemId: DISMISS_RECOMMENDATION,
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
		);
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
				recommendationSession: {
					id: sessionId ?? '',
					type: 'appRec',
				},
			},
			{
				navigationVersion: 'v4',
			},
		);
		dismissRecommendation();
		if (accountId) {
			setUserProperties(accountId, JPD_RECOMMENDATION_DISMISS_USER_PROPERTY, 'true').catch(
				(error) => {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'dismissJpdRecommendation',
							packageName: PACKAGE_NAME,
							teamName: 'navigation',
						},
					});
				},
			);
		}
	}, [
		accountId,
		createAnalyticsEvent,
		dismissRecommendation,
		fireInteractedUIEvent,
		jpdCloudCCPProductID,
		sessionId,
	]);

	const openCrossFlow = useCallback(
		async (templateId: string) => {
			const extensions = createProvisioningPageExtensions({
				actions: {
					primary: {
						behaviour: Behaviours.LAND,
						prompt: Prompts.NEXT,
						options: {
							targetTab: TargetTabs.SAME,
							urlParams: { templateId },
						},
					},
				},
			});

			if (crossFlow.isEnabled) {
				// eslint-disable-next-line @atlassian/growth/no-deprecated-apis
				crossFlow.api.open({
					journey: Journeys.GET_STARTED,
					targetProduct: Targets.JIRA_PRODUCT_DISCOVERY,
					sourceComponent: 'projectsMenu',
					sourceContext: 'tryThisRecommendation',
					extensions,
				});
			}
		},
		[crossFlow],
	);

	const onRecommendationClicked = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: NAVIGATION_ITEM,
				action: 'clicked',
			}),
			PROJECTS_NAVIGATION_ITEM,
			{
				level: LEVEL_ONE,
				componentType: CUSTOM_ITEM,
				itemType: PROJECT_RECOMMENDATION,
				itemId: NEW_DISCOVERY_PROJECT,
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
			},
		);
		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryThisRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: jpdCloudCCPProductID,
				recommendedProductIds: [jpdCloudCCPProductID],
				recommendationSession: {
					id: sessionId ?? '',
					type: 'appRec',
				},
			},
			{
				navigationVersion: 'v4',
			},
		);
		openCrossFlow(jpdRecommendationContent.projectKey);
	}, [
		createAnalyticsEvent,
		fireInteractedUIEvent,
		jpdCloudCCPProductID,
		jpdRecommendationContent.projectKey,
		openCrossFlow,
		sessionId,
	]);

	const recommendationActionMenuClickHandler = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'button',
				action: 'clicked',
			}),
			PROJECTS_NAVIGATION_ITEM,
			{
				level: LEVEL_TWO,
				componentType: CUSTOM_ITEM_BUTTON,
				parentItemType: PROJECT_RECOMMENDATION,
				parentItemId: NEW_DISCOVERY_PROJECT,
				itemId: MEATBALL_DROPDOWN,
			},
		);
	}, [createAnalyticsEvent]);
	return (
		<>
			<Nav4MenuLinkItem<JiraLinkConfig>
				elemBefore={<jpdRecommendationContent.projectAvatar />}
				elemAfter={
					<Lozenge appearance="discovery" isBold={false}>
						{formatMessage(lozengeText)}
					</Lozenge>
				}
				href=""
				menuId={recommendationKey}
				onClick={onRecommendationClicked}
				actionsOnHover={
					<RecommendationActionMenu
						onClick={recommendationActionMenuClickHandler}
						onLearnMoreClicked={onLearnMoreClicked}
						onDismissClicked={onDismissClicked}
						primaryCta={formatMessage(reasonsModalButtonOk)}
						body={
							<>
								{formatMessage(reasonsModalExplanation, {
									strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
								<ul>
									<li>{formatMessage(reasonsModalBulletOneSpork)}</li>
									<li>{formatMessage(reasonsModalBulletTwoAppify)}</li>
								</ul>
								<br />
								<Button appearance="link" spacing="none" href={DATA_PRIVACY_LINK} target="_blank">
									{formatMessage(privacyPolicy)}
								</Button>
							</>
						}
						title={formatMessage(reasonsModalTitle)}
					/>
				}
			>
				{formatMessage(jpdRecommendationContent.projectTitle)}
			</Nav4MenuLinkItem>
		</>
	);
};

const checkEligibility = (
	recommendationContext: RecommendationContext,
	appRecommendations: AppRecommendations,
) => {
	const { areProductSuggestionsEnabled, licensedProducts, accountId, jpdRecommendationDismissed } =
		recommendationContext;

	if (!accountId) {
		return { isEligible: false, reason: 'Account Id is null' };
	}

	if (!areProductSuggestionsEnabled) {
		return { isEligible: false, reason: 'Product suggestions are disabled' };
	}

	if (hasJpdLicense(licensedProducts)) {
		return { isEligible: false, reason: 'Instance has JPD license' };
	}

	if (jpdRecommendationDismissed) {
		return { isEligible: false, reason: 'JPD recommendation dismissed' };
	}

	if (!appRecommendations?.some((entityId) => entityId === JPD_ENTITY_ID)) {
		return { isEligible: false, reason: 'JPD was not in the recommend apps' };
	}

	return { isEligible: true, reason: 'Pass all checks' };
};

export const jpdRecommendation: {
	key: string;
	shouldRender: (
		recommendationContext: RecommendationContext,
		analyticsEvent: UIAnalyticsEvent,
		appRecommendations: AppRecommendations,
	) => boolean;
	Component: ({
		recommendationKey,
		dismissRecommendation,
		sessionId,
	}: {
		recommendationKey: string;
		dismissRecommendation: () => void;
		sessionId?: string | null;
	}) => React.JSX.Element;
} = {
	key: JPD_RECOMMENDATION_KEY,
	shouldRender: (
		recommendationContext: RecommendationContext,
		analyticsEvent: UIAnalyticsEvent,
		appRecommendations: AppRecommendations,
	): boolean => {
		const { isEligible, reason } = checkEligibility(recommendationContext, appRecommendations);
		if (fg('pm_jpd_reco_operational_event_activated')) {
			fireOperationalAnalytics(
				analyticsEvent,
				'jiraAtlassianNavigation.loadJpdRecommendation succeeded',
				{
					reason,
					isEligible,
				},
			);
		}

		return isEligible;
	},
	Component: JpdRecommendation,
};
